import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import PoiEditor from "./PoiEditor";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  normalizeGpxPointsInput,
  findNearestGpxPointIndex,
  flattenSequencedGpxFiles,
  sliceGpxSubset,
} from "../lib/gpx";
import MapView from "./MapView";

export default function VideoForm({
  value,
  onChange,
  routes,
  playlists = [],
  lockedRouteID = "",
  lockedPlaylistID = "",
  showRouteField = true,
  showPlaylistField = true,
  showStatusField = true,
  showCreatorField = true,
  showDurationField = true,
  parseUploadedGpx,
  fetchVideoMetadata,
}) {
  const v = value || {};
  const status = String(v?.status || "").trim() || "created";
  const set = (key, nextValue) => onChange({ ...v, [key]: nextValue });
  const effectiveRouteID = lockedRouteID || v.route_id || "";
  const effectivePlaylistID =
    lockedPlaylistID || v.playlist_id || v.group_id || "";
  const selectedRoute = routes.find((route) => route.id === effectiveRouteID);
  const selectedPlaylist = playlists.find(
    (playlist) => playlist.id === effectivePlaylistID,
  );
  const playlistOptions = lockedRouteID
    ? playlists.filter((playlist) => playlist.route_id === lockedRouteID)
    : playlists;
  const routeMapPoints = selectedRoute?.gpx_points || [];
  const playlistMapPoints = useMemo(() => {
    const sequenced = flattenSequencedGpxFiles(
      selectedPlaylist?.gpx_files || [],
    );
    return sequenced.length > 0
      ? sequenced
      : normalizeGpxPointsInput(selectedPlaylist?.gpx_points || []);
  }, [selectedPlaylist]);
  const fallbackMapPoints =
    Array.isArray(playlistMapPoints) && playlistMapPoints.length > 1
      ? playlistMapPoints
      : routeMapPoints;
  const gpxdataPoints = normalizeGpxPointsInput(v?.gpxdata);
  const legacyVideoPoints = normalizeGpxPointsInput(
    v?.gpx_points || v?.gpxPoints || [],
  );
  const videoOwnGpxPoints =
    gpxdataPoints.length > 0 ? gpxdataPoints : legacyVideoPoints;
  const hasVideoOwnGpx = videoOwnGpxPoints.length > 1;
  const videoMapPoints = hasVideoOwnGpx ? videoOwnGpxPoints : fallbackMapPoints;
  const inheritedCreator =
    selectedPlaylist?.creator_name || selectedPlaylist?.creatorName;
  const [subsetStartIndex, setSubsetStartIndex] = useState(null);
  const [subsetEndIndex, setSubsetEndIndex] = useState(null);
  const [isParsingVideoGPX, setIsParsingVideoGPX] = useState(false);
  const [lastVideoGPXFile, setLastVideoGPXFile] = useState("");
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);

  useEffect(() => {
    setSubsetStartIndex(null);
    setSubsetEndIndex(null);
  }, [effectiveRouteID, v?.id]);

  const selectedSubsetSegment = useMemo(() => {
    if (
      subsetStartIndex == null ||
      subsetEndIndex == null ||
      !Array.isArray(fallbackMapPoints) ||
      fallbackMapPoints.length === 0
    ) {
      return null;
    }

    const startPoint = fallbackMapPoints[subsetStartIndex];
    const endPoint = fallbackMapPoints[subsetEndIndex];
    if (!Array.isArray(startPoint) || !Array.isArray(endPoint)) return null;

    return {
      start: [Number(startPoint[0]) || 0, Number(startPoint[1]) || 0],
      end: [Number(endPoint[0]) || 0, Number(endPoint[1]) || 0],
    };
  }, [subsetStartIndex, subsetEndIndex, fallbackMapPoints]);

  const handleSubsetMapPick = ({ lat, lng }) => {
    if (
      hasVideoOwnGpx ||
      !Array.isArray(fallbackMapPoints) ||
      fallbackMapPoints.length < 2
    ) {
      return;
    }
    const index = findNearestGpxPointIndex(
      fallbackMapPoints,
      Number(lat) || 0,
      Number(lng) || 0,
    );
    if (index < 0) return;

    if (
      subsetStartIndex == null ||
      (subsetStartIndex != null && subsetEndIndex != null)
    ) {
      setSubsetStartIndex(index);
      setSubsetEndIndex(null);
      return;
    }

    setSubsetEndIndex(index);
  };

  const applyRouteSubsetToVideo = () => {
    if (
      hasVideoOwnGpx ||
      subsetStartIndex == null ||
      subsetEndIndex == null ||
      !Array.isArray(fallbackMapPoints) ||
      fallbackMapPoints.length < 2
    ) {
      return;
    }

    const subset = sliceGpxSubset(
      fallbackMapPoints,
      subsetStartIndex,
      subsetEndIndex,
    );
    if (subset.length < 2) {
      toast.error("Selected GPX subset must include at least 2 points");
      return;
    }

    onChange({
      ...v,
      gpxdata: subset,
      gpx_points: subset,
      gpxPoints: subset,
    });
    toast.success(`Copied ${subset.length} GPX points from route to video`);
  };

  const handleVideoGpxUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !parseUploadedGpx) return;

    setIsParsingVideoGPX(true);
    try {
      const parsed = await parseUploadedGpx(file);
      const parsedPoints = normalizeGpxPointsInput(
        parsed?.gpx_points || parsed,
      );
      onChange({
        ...v,
        gpxdata: parsedPoints,
        gpx_points: parsedPoints,
        gpxPoints: parsedPoints,
      });
      setLastVideoGPXFile(file.name);
      setSubsetStartIndex(null);
      setSubsetEndIndex(null);
      toast.success("Video GPX imported");
    } catch {
      toast.error("Failed to parse video GPX file");
    } finally {
      setIsParsingVideoGPX(false);
      event.target.value = "";
    }
  };

  const handleVideoUrlBlur = async (url) => {
    const trimmed = String(url || "").trim();
    if (!trimmed || !fetchVideoMetadata) return;

    setIsFetchingMetadata(true);
    try {
      const metadata = await fetchVideoMetadata(trimmed);
      if (!metadata) return;
      onChange({
        ...v,
        video_url: trimmed,
        videoURL: trimmed,
        title: v.title || metadata.title || v.title,
        thumbnail: v.thumbnail || metadata.thumbnail || v.thumbnail,
        duration:
          Number(v.duration) > 0
            ? v.duration
            : Number(metadata.duration) > 0
              ? metadata.duration
              : v.duration,
      });
    } catch {
      // Ignore metadata failures and preserve manually entered values.
    } finally {
      setIsFetchingMetadata(false);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      {showRouteField && (
        <div className="col-span-2">
          <Label>Route</Label>
          <Select
            value={effectiveRouteID}
            onValueChange={(nextValue) => set("route_id", nextValue)}
            disabled={!!lockedRouteID}
          >
            <SelectTrigger>
              <SelectValue placeholder="Pick a route" />
            </SelectTrigger>
            <SelectContent>
              {routes.map((route) => (
                <SelectItem key={route.id} value={route.id}>
                  {route.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {showPlaylistField && (
        <div>
          <Label>Playlist (optional)</Label>
          <Select
            value={effectivePlaylistID || "__none__"}
            onValueChange={(nextValue) => {
              const resolvedValue = nextValue === "__none__" ? "" : nextValue;
              onChange({
                ...v,
                playlist_id: resolvedValue,
                group_id: resolvedValue,
              });
            }}
            disabled={!!lockedPlaylistID}
          >
            <SelectTrigger>
              <SelectValue placeholder="Pick a playlist (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">(none)</SelectItem>
              {playlistOptions.map((playlist) => (
                <SelectItem key={playlist.id} value={playlist.id}>
                  {playlist.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {showCreatorField && inheritedCreator && (
        <div>
          <Label>Inherited Creator</Label>
          <div className="px-3 py-2 bg-[#F4EFE6] rounded-lg text-sm text-[#2C5545] font-medium">
            {inheritedCreator}
          </div>
        </div>
      )}
      <div className="col-span-2">
        <Label>Title</Label>
        <Input
          value={v.title || ""}
          onChange={(event) => set("title", event.target.value)}
        />
      </div>
      {showStatusField && (
        <div>
          <Label>Status (read-only)</Label>
          <div className="px-3 py-2 bg-[#F4EFE6] rounded-lg text-sm text-[#2C5545] font-medium capitalize">
            {status}
          </div>
        </div>
      )}
      {showCreatorField && (
        <div>
          <Label>Creator</Label>
          <Input
            value={v.creator || ""}
            onChange={(event) => set("creator", event.target.value)}
            placeholder="Auto-filled from playlist if set"
          />
        </div>
      )}
      {showDurationField && (
        <div>
          <Label>Duration (s)</Label>
          <Input
            type="number"
            value={v.duration ?? v.length ?? v.video_length ?? 0}
            onChange={(event) =>
              set("duration", parseFloat(event.target.value) || 0)
            }
          />
        </div>
      )}
      <div className="col-span-2">
        <Label>Video URL (mp4 or YouTube)</Label>
        <Input
          value={v.video_url || v.videoUrl || ""}
          onChange={(event) =>
            onChange({
              ...v,
              video_url: event.target.value,
              videoURL: event.target.value,
            })
          }
          onBlur={(event) => handleVideoUrlBlur(event.target.value)}
        />
        <div className="text-xs text-[#5C665F] mt-2">
          {isFetchingMetadata
            ? "Fetching video details..."
            : "Paste a YouTube URL to auto-fill title and thumbnail from your video metadata."}
        </div>
      </div>
      <div className="col-span-2">
        <Label>Thumbnail URL</Label>
        <Input
          value={v.thumbnail || ""}
          onChange={(event) => set("thumbnail", event.target.value)}
        />
      </div>

      <div className="col-span-2 rounded-xl border border-[#E5E0D8] bg-[#FCFAF6] p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-[#1A1D1A]">
              Video GPX track
            </div>
            <div className="text-xs text-[#5C665F]">
              {hasVideoOwnGpx
                ? `Using ${videoOwnGpxPoints.length} video-specific GPX points.`
                : "No video-specific GPX yet. Upload a GPX file or pick start/end on playlist/route map to copy a subset."}
            </div>
          </div>
          {hasVideoOwnGpx && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                onChange({
                  ...v,
                  gpxdata: [],
                  gpx_points: [],
                  gpxPoints: [],
                });
                setSubsetStartIndex(null);
                setSubsetEndIndex(null);
              }}
            >
              Clear video GPX
            </Button>
          )}
        </div>

        <div>
          <Label>Upload video GPX file (optional)</Label>
          <Input
            type="file"
            accept=".gpx,application/gpx+xml,application/xml,text/xml"
            onChange={handleVideoGpxUpload}
            disabled={isParsingVideoGPX}
          />
          <div className="text-xs text-[#5C665F] mt-2">
            {isParsingVideoGPX
              ? "Parsing video GPX file..."
              : `${hasVideoOwnGpx ? `Loaded points: ${videoOwnGpxPoints.length}` : "No video GPX uploaded"}${lastVideoGPXFile ? ` (last file: ${lastVideoGPXFile})` : ""}`}
          </div>
        </div>

        {!hasVideoOwnGpx && (
          <>
            <div className="rounded-lg overflow-hidden border border-[#E5E0D8] h-56 bg-white">
              {Array.isArray(fallbackMapPoints) &&
              fallbackMapPoints.length > 1 ? (
                <MapView
                  gpxPoints={fallbackMapPoints}
                  markerSegment={selectedSubsetSegment}
                  onMapClick={handleSubsetMapPick}
                  height="100%"
                  fit
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-sm text-[#5C665F] px-4 text-center">
                  Select a route with GPX data to choose a video track subset.
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
              <div>
                <Label>Start point index</Label>
                <Input value={subsetStartIndex ?? ""} readOnly />
              </div>
              <div>
                <Label>End point index</Label>
                <Input value={subsetEndIndex ?? ""} readOnly />
              </div>
              <Button
                type="button"
                size="sm"
                disabled={subsetStartIndex == null || subsetEndIndex == null}
                onClick={applyRouteSubsetToVideo}
              >
                Use selected route segment
              </Button>
            </div>
            <div className="text-xs text-[#5C665F]">
              Click once to set start, click again to set end. Clicking a third
              time starts a new selection. Source:{" "}
              {Array.isArray(playlistMapPoints) && playlistMapPoints.length > 1
                ? "playlist GPX"
                : "route GPX"}
              .
            </div>
          </>
        )}
      </div>
      <div className="col-span-2">
        <PoiEditor
          title="Video map & POIs"
          gpxPoints={videoMapPoints}
          secondaryGpxPoints={hasVideoOwnGpx ? fallbackMapPoints : []}
          pois={v.pois || []}
          onChange={(nextPois) => set("pois", nextPois)}
        />
      </div>
    </div>
  );
}
